import { useRef, useState } from "react";
import { toast } from "sonner";
import { Download, RotateCcw, Upload } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { FormAlert } from "@/components/common/Alert";
import { EntityAvatar } from "@/components/catalogue/EntityAvatar";
import { InputsEditor, FieldsEditor, validateRows, normaliseFields } from "@/pages/admin/RowEditors";
import { useCatalogue } from "@/context/CatalogueContext";
import { api, errorMessage, downloadFile } from "@/lib/api";

const f = (k, label, type = "text", path = k) => ({ k, label, type, path });
const WF_MATCH = [
  f("mindpal_workflow_id", "MindPal workflow id (24 characters, used to match the Webhook node)", "text", "mindpal.workflow_id"),
  f("mindpal_workflow_title", "MindPal workflow title (fallback match)", "text", "mindpal.workflow_title"),
];
const WF_ID_RE = /^[A-Za-z0-9]{24}$/;
const COMMON_TAIL = [
  f("avatar_url", "Avatar image URL (https)"), f("display_order", "Display order", "number"),
  f("published", "Published", "bool"), f("visible", "Visible to other users", "bool"), f("viewer_can_run", "Viewer can run", "bool"),
];
export const FIELDS = {
  agents: [
    f("chosen_name", "Chosen name"), f("kind", "Kind (short label)"), f("model", "Model"), f("function", "Function (one line on the card)", "textarea"),
    f("key_benefit", "Key benefit", "textarea"), f("use_me_for", "Use me for", "textarea"), f("what_you_get", "What you get", "textarea"),
    f("description", "Description", "textarea"), f("bio", "About (bio)", "textarea"),
    f("inputs_summary", "What it needs", "inputs"), f("form_fields", "Form fields", "fields"),
    f("shape_chat", "Chatbot shape on (users see the chat version)", "bool", "shape_enabled.chat"), f("shape_form", "Form shape on (users see the form version)", "bool", "shape_enabled.form"),
    f("chatbot_slug", "MindPal chatbot slug", "text", "mindpal.chatbot_slug"), f("workflow_slug", "MindPal workflow slug", "text", "mindpal.workflow_slug"),
    ...WF_MATCH, ...COMMON_TAIL,
  ],
  workflows: [
    f("name", "Name"), f("one_line", "One line", "textarea"), f("key_benefit", "Key benefit (on the card)", "textarea"),
    f("use_me_for", "Use me for", "textarea"), f("what_you_get", "What you get", "textarea"), f("bio", "About", "textarea"), f("description", "Description", "textarea"),
    f("trigger", "Trigger"), f("schedule", "Schedule"), f("human_gates", "Approval gates (one per line)", "lines"),
    f("trigger_fields", "Trigger fields", "fields"),
    f("workflow_slug", "MindPal workflow slug", "text", "mindpal.workflow_slug"), ...WF_MATCH, ...COMMON_TAIL,
  ],
  teams: [f("name", "Name"), f("section", "Section name (navigation)"), f("purpose", "Purpose", "textarea"), f("order", "Order", "number"), f("published", "Published", "bool")],
};
export const KEY = { agents: "emp_no", workflows: "id", teams: "id" };
const getPath = (d, p) => p.split(".").reduce((o, k) => (o ? o[k] : undefined), d);
const initial = (fd, d) => {
  const v = getPath(d, fd.path);
  if (fd.type === "bool") return ["visible", "shape_chat", "shape_form"].includes(fd.k) ? v !== false : !!v;
  if (fd.type === "lines") return (v || []).join("\n");
  if (fd.type === "inputs" || fd.type === "fields") return v || [];
  if (fd.type === "number") return v ?? 0;
  return v || "";
};

const EditedDot = ({ doc, fd, onReset }) => (doc.edited_fields || []).includes(fd.path) ? (
  <span className="inline-flex items-center gap-2">
    <span className="inline-flex items-center gap-1 text-xs text-action" data-testid={`edited-dot-${fd.k}`}><span className="h-1.5 w-1.5 rounded-full bg-action" aria-hidden="true" />edited in hub</span>
    <button type="button" onClick={() => onReset(fd.path)} className="inline-flex items-center gap-1 text-xs underline underline-offset-4" data-testid={`reset-${fd.k}`}><RotateCcw className="!h-3 !w-3" aria-hidden="true" />reset to imported value</button>
  </span>
) : null;

const FieldInput = ({ fd, value, set }) => {
  const id = `edit-${fd.k}`;
  if (fd.type === "bool") return <Switch id={id} checked={value} onCheckedChange={set} data-testid={`catalogue-edit-${fd.k}`} />;
  if (fd.type === "inputs") return <InputsEditor rows={value} onChange={set} />;
  if (fd.type === "fields") return <FieldsEditor rows={value} onChange={set} label={fd.label} testid={`${fd.k}-editor`} />;
  if (fd.type === "textarea" || fd.type === "lines") return <Textarea id={id} rows={3} value={value} onChange={(e) => set(e.target.value)} className="rounded-xl" data-testid={`catalogue-edit-${fd.k}`} />;
  return <Input id={id} type={fd.type} value={value} onChange={(e) => set(e.target.value)} data-testid={`catalogue-edit-${fd.k}`} />;
};

export const EditDialog = ({ edit, onClose }) => {
  const { reload } = useCatalogue();
  const [form, setForm] = useState({});
  const [doc, setDoc] = useState(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);
  const cur = doc || edit?.doc;
  const fields = edit ? FIELDS[edit.coll] : [];
  const key = cur ? cur[KEY[edit.coll]] : "";
  const values = cur ? Object.fromEntries(fields.map((fd) => [fd.k, fd.k in form ? form[fd.k] : initial(fd, cur)])) : {};
  const close = () => { setForm({}); setDoc(null); setError(""); onClose(); };

  const convert = (fd, v) => (fd.type === "number" ? Number(v) : fd.type === "lines" ? v.split("\n").map((s) => s.trim()).filter(Boolean) : fd.type === "fields" ? normaliseFields(v) : v);
  const save = async (e) => {
    e.preventDefault();
    const changed = fields.filter((fd) => fd.k in form && JSON.stringify(form[fd.k]) !== JSON.stringify(initial(fd, cur)));
    for (const fd of changed) {
      if (fd.type === "inputs" || fd.type === "fields") {
        const msg = validateRows(fd.type === "inputs" ? "inputs_summary" : "form_fields", form[fd.k]);
        if (msg) return setError(msg);
      }
      if (fd.k === "mindpal_workflow_id" && form[fd.k].trim() && !WF_ID_RE.test(form[fd.k].trim())) return setError("A MindPal workflow id is exactly 24 letters and numbers.");
    }
    if (!changed.length) return close();
    setBusy(true);
    setError("");
    try {
      await api.patch(`/admin/catalogue/${edit.coll}/${key}`, Object.fromEntries(changed.map((fd) => [fd.k, convert(fd, form[fd.k])])));
      toast.success("Saved. A later import will keep these edits.");
      await reload();
      close();
    } catch (err) { setError(errorMessage(err)); } finally { setBusy(false); }
  };
  const reset = async (field) => {
    try {
      const { data } = await api.post(`/admin/catalogue/${edit.coll}/${key}/reset`, { field });
      setDoc(data);
      setForm((fm) => { const n = { ...fm }; delete n[fields.find((fd) => fd.path === field)?.k]; return n; });
      toast.success("Reset to the imported value");
      reload();
    } catch (err) { toast.error(errorMessage(err)); }
  };
  const uploadAvatar = async (file) => {
    if (!file) return;
    const body = new FormData();
    body.append("file", file);
    try { const { data } = await api.post(`/admin/catalogue/${edit.coll}/${key}/avatar`, body); setDoc(data); reload(); toast.success("Avatar updated"); } catch (err) { toast.error(errorMessage(err)); }
  };

  return (
    <Dialog open={!!edit} onOpenChange={(o) => !o && close()}>
      <DialogContent className="max-h-[90vh] overflow-y-auto rounded-2xl sm:max-w-3xl" data-testid="catalogue-edit-dialog">
        <DialogHeader>
          <DialogTitle>Edit {edit?.name}</DialogTitle>
          <DialogDescription>Fields you change are marked and protected from later imports unless you choose overwrite.</DialogDescription>
        </DialogHeader>
        {cur && edit.coll !== "teams" && (
          <div className="flex flex-wrap items-center gap-3 rounded-xl border p-3">
            <EntityAvatar entity={cur} name={edit.name} size="lg" />
            <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/webp" className="sr-only" aria-label="Avatar image" onChange={(e) => uploadAvatar(e.target.files?.[0])} data-testid="catalogue-avatar-input" />
            <Button type="button" variant="secondary" size="sm" onClick={() => fileRef.current?.click()} data-testid="catalogue-avatar-upload"><Upload className="!h-4 !w-4" /> Upload avatar</Button>
            {cur.name_suggestion && <p className="text-sm text-muted-foreground" data-testid="catalogue-name-suggestion">Name suggestion: {cur.name_suggestion}</p>}
            <Button type="button" variant="ghost" size="sm" className="ml-auto" onClick={() => downloadFile(`/admin/catalogue/export/${edit.coll}/${key}`, `${key}.json`)} data-testid="catalogue-export"><Download className="!h-4 !w-4" /> Export</Button>
          </div>
        )}
        <form onSubmit={save} className="space-y-4">
          <FormAlert testid="catalogue-edit-error">{error}</FormAlert>
          {fields.map((fd) => (
            <div key={fd.k} className={fd.type === "bool" ? "flex items-center justify-between gap-3 rounded-xl border px-3 py-2.5" : "space-y-2"}>
              <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
                {fd.type !== "inputs" && fd.type !== "fields" && <Label htmlFor={`edit-${fd.k}`}>{fd.label}</Label>}
                <EditedDot doc={cur} fd={fd} onReset={reset} />
              </div>
              <FieldInput fd={fd} value={values[fd.k]} set={(v) => setForm((fm) => ({ ...fm, [fd.k]: v }))} />
            </div>
          ))}
          <DialogFooter>
            <Button type="button" variant="ghost" onClick={close} data-testid="catalogue-edit-cancel">Cancel</Button>
            <Button type="submit" disabled={busy} data-testid="catalogue-edit-save">{busy ? "Saving" : "Save"}</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
